import { Spinner } from '@Components/common/Spinner';
import SidebarList from '@Components/SidebarList';
import { Icon, ListLink, Ul } from '@Components/SidebarList/SidebarList.styled';
import { useAuth } from '@Contexts/AuthContext/Auth.context';
import useTranslation from 'next-translate/useTranslation';
import Image from 'next/image';
import { useRouter } from 'next/router';
import Pin from 'public/address.svg';
import User from 'public/address_user.svg';
import Car from 'public/car.svg';
import Chat from 'public/chat.svg';
import Edit from 'public/edit_user.svg';
import Heart from 'public/heart.svg';
import Hotline from 'public/hotline.svg';
import Key from 'public/key.svg';
import Letter from 'public/letter.svg';
import Logout from 'public/logout.svg';
import Products from 'public/my_products.svg';
import Translate from 'public/translate.svg';
import { useState } from 'react';
import {
  Div,
  Header,
  SidebarWrapper,
  Toggle,
  UserData,
  UserEmail,
  UserImage,
  UserName,
  Wrapper,
} from './SideBar.styled';

type Props = {
  show?: boolean;
};

export default function Sidebar({ show }: Props) {
  const [isOpen, setIsOpen] = useState(show);
  const { t } = useTranslation('Profile');
  const { userData, isUserFetching } = useAuth();
  const [imgSrc, setImgSrc] = useState<null | string>(userData?.avatar || null);
  const { pathname } = useRouter();

  return (
    <Div open={isOpen}>
      <SidebarWrapper>
        <Header>
          {isUserFetching ? (
            <Spinner />
          ) : (
            <>
              {imgSrc === null ? (
                <UserImage as="div">{userData?.full_name[0]}</UserImage>
              ) : (
                <UserImage src={imgSrc} alt={userData?.full_name} onError={() => setImgSrc(null)} />
              )}
              <UserData>
                <UserName>{userData?.full_name}</UserName>
                <UserEmail>{userData?.email}</UserEmail>
              </UserData>
            </>
          )}
        </Header>
        <Wrapper>
          <SidebarList
            icon="/user.svg"
            listItem={t('personal_account')}
            defaultOpen={['/profile/cars', '/profile/edit', '/profile/addresses', '/profile/wishlist'].includes(
              pathname
            )}
          >
            <Ul>
              <ListLink active={pathname === '/profile/cars'} href="/profile/cars">
                <Icon>
                  <Image src={Car} width={17.94} height={18} alt="user" />
                </Icon>
                {t('my_cars')}
              </ListLink>
              <ListLink active={pathname === '/profile/edit'} href="/profile/edit">
                <Icon>
                  <Image src={Edit} width={17.94} height={18} alt="user" />
                </Icon>
                {t('edit_profile')}
              </ListLink>
              <ListLink active={pathname === '/profile/addresses'} href="/profile/addresses">
                <Icon>
                  <Image src={Pin} width={17.94} height={18} alt="user" />
                </Icon>
                {t('my_addresses')}
              </ListLink>
              <ListLink active={pathname === '/profile/wishlist'} href="/profile/wishlist">
                <Icon>
                  <Image src={Heart} width={17.94} height={18} alt="user" />
                </Icon>
                {t('my_favorites')}
              </ListLink>
            </Ul>
          </SidebarList>
          <SidebarList
            icon="/bag.svg"
            listItem={t('my_purchases')}
            defaultOpen={[
              // '/profile/reservations',
              '/profile/myproducts',
              '/profile/refund',
              // '/profile/myreviews',
              '/profile/myorders',
              '/profile/orders',
              '/profile/hotline',
              '/profile/customerservice',
              '/profile/help',
            ].includes(pathname)}
          >
            <Ul>
              {/* <ListLink active={pathname === '/profile/reservations'} href="/profile/reservations">
                <Icon>
                  <Image src={Booking} width={17.94} height={18} alt="Booking" />
                </Icon>
                {t('reservations')}
              </ListLink> */}
              <ListLink active={pathname.includes('/profile/orders')} href="/profile/orders">
                <Icon>
                  <Image src={Products} width={17.94} height={18} alt="Products" />
                </Icon>
                {t('my_products')}
              </ListLink>
              {/* <ListLink active={pathname === '/profile/refund'} href="/profile/refund">
                <Icon>
                  <Image src={Refund} width={17.94} height={18} alt="Refund" />
                </Icon>
                {t('refund')}
              </ListLink> */}
              {/* <ListLink active={pathname === '/profile/myreviews'} href="/profile/myreviews">
                <Icon>
                  <Image src={Review} width={17.94} height={18} alt="Review" />
                </Icon>
                {t('my_reviews')}
              </ListLink> */}
            </Ul>
          </SidebarList>
          <ListLink parent active={pathname === '/profile/missing-products'} href="/profile/missing-products">
            <Icon>
              <Image src={Letter} width={17.94} height={18} alt="user" />
            </Icon>
            {t('my_orders')}
          </ListLink>
          <SidebarList
            icon="/headphone.svg"
            listItem={t('help_center')}
            defaultOpen={['/profile/hotline', '/profile/customerservice', '/profile/help'].includes(pathname)}
          >
            <Ul>
              <ListLink active={pathname === '/profile/hotline'} href="/profile/hotline">
                <Icon>
                  <Image src={Hotline} width={17.94} height={18} alt="user" />
                </Icon>
                {t('hotline')}
              </ListLink>
              <ListLink active={pathname === '/profile/customerservice'} href="/profile/customerservice">
                <Icon>
                  <Image src={Chat} width={17.94} height={18} alt="user" />
                </Icon>
                {t('customer_service')}
              </ListLink>
              {/* <ListLink active={pathname === '/profile/help'} href="/profile/help">
                <Icon>
                  <Image src={Help} width={17.94} height={18} alt="user" />
                </Icon>
                {t('q&a')}
              </ListLink> */}
            </Ul>
          </SidebarList>
          <SidebarList
            icon="/settings.svg"
            listItem={t('settings')}
            defaultOpen={[
              '/profile/changepass',
              '/profile/translate',
              '/profile/about',
              '/profile/terms',
              '/profile/logout',
            ].includes(pathname)}
          >
            <Ul>
              <ListLink active={pathname === '/profile/changepass'} href="/profile/changepass">
                <Icon>
                  <Image src={Key} width={24} height={24} alt="key" />
                </Icon>
                {t('change_password')}
              </ListLink>
              <ListLink active={pathname === '/profile/translate'} href="/profile/translate">
                <Icon>
                  <Image src={Translate} width={24} height={24} alt="user" />
                </Icon>
                {t('change_language')}
              </ListLink>
            </Ul>
          </SidebarList>
          <ListLink parent active={pathname === '/profile/logout'} href="/profile/logout">
            <Icon>
              <Image src={Logout} width={17.94} height={18} alt="logout" />
            </Icon>
            {t('logout')}
          </ListLink>
        </Wrapper>
      </SidebarWrapper>
      <Toggle onClick={() => setIsOpen(!isOpen)}>
        <Image src={User} width={18} height={18} alt="user" />
      </Toggle>
    </Div>
  );
}
